/**
 * Authentication context for KSeF frontend.
 * Uses @eoln/sdk-auth for API key validation.
 */

import { AuthClient } from "@eoln/sdk-auth";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { clearSession, getSession, hasSession, setSession } from "./session.ts";

const AUTH_ENDPOINT = import.meta.env.VITE_AUTH_GRAPHQL_ENDPOINT;
if (!AUTH_ENDPOINT) {
  // No fallback: a missing build var must fail the build, not validate prod
  // keys against dev. Local dev sets it in .env.development.
  throw new Error("VITE_AUTH_GRAPHQL_ENDPOINT is not set");
}

interface AuthContextValue {
  readonly isAuthenticated: boolean;
  readonly apiKey: string | null;
  readonly login: (apiKey: string) => Promise<void>;
  readonly logout: () => void;
  readonly client: AuthClient | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());
  const [apiKey, setApiKey] = useState<string | null>(getSession());

  const client = useMemo(() => (apiKey ? new AuthClient({ endpoint: AUTH_ENDPOINT, apiKey }) : null), [apiKey]);

  const login = useCallback(async (newApiKey: string) => {
    const apiKeyPattern = /^sk_[0-9a-f]{64}$/;
    if (!apiKeyPattern.test(newApiKey)) {
      throw new Error("Invalid API key format. Expected: sk_ followed by 64 hex characters");
    }

    const tempClient = new AuthClient({
      endpoint: AUTH_ENDPOINT,
      apiKey: newApiKey,
    });
    const authInfo = await tempClient.getAuthInfo();
    if (!authInfo.isAuthenticated) {
      throw new Error("API key is not valid or has been revoked");
    }

    setSession(newApiKey);
    setApiKey(newApiKey);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    // Drop every cached query first: the next login on this tab must not see
    // the previous key's credential status or invoices.
    queryClient.clear();
    clearSession();
    setApiKey(null);
    setIsAuthenticated(false);
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, apiKey, login, logout, client }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
